import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  Tabs,
  Tab
} from '@mui/material'
import { Refresh, Check, Close } from '@mui/icons-material'
import * as actions from '../../actions.es6.js'

const navy = '#173E4B'

// Rating changes ride a separate maker-checker lane from product changes (see
// CLAUDE.md → "Maker-checker approval gate" / rating engine section) — split
// the queue so approvers can filter to just one review lane. `undefined`
// means no domain filter (both together).
const DOMAIN_TABS = [
  { value: undefined, label: 'All' },
  { value: 'product', label: 'Product' },
  { value: 'rating', label: 'Rating' }
]

const TH = {
  fontSize: 11,
  fontWeight: 700,
  color: '#6B7280',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  bgcolor: '#F9FAFB',
  borderBottom: '1px solid #E5E7EB'
}

const STATUS_STYLE = {
  Pending: { bg: '#FFF7ED', color: '#9A3412' },
  Approved: { bg: '#F0FDF4', color: '#15803D' },
  Rejected: { bg: '#FEF2F2', color: '#B91C1C' },
  Stale: { bg: '#F3F4F6', color: '#6B7280' },
  Failed: { bg: '#FEF2F2', color: '#B91C1C' }
}

function fmtDate(d) {
  if (!d) return '—'
  try {
    return new Date(d).toLocaleString()
  } catch (e) {
    return String(d)
  }
}

function ChangeRequestsScreen(props) {
  const { dispatch } = props
  const data = props.genericProductsData || {}
  const crs = data.changeRequests || []
  const [domain, setDomain] = useState(undefined)

  useEffect(() => {
    dispatch(actions.getChangeRequestsRequest({ domain }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain])

  const resolve = (guid, decision) => {
    dispatch(actions.resolveChangeRequestRequest({ guid, decision, domain }))
  }

  const feedbackOpen = Boolean(data.changeRequestActionSuccess || data.changeRequestActionError)

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1f2937', flex: 1 }}>
          Change Requests
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            maker-checker approvals across all products
          </Typography>
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={() => dispatch(actions.getChangeRequestsRequest({ domain }))} sx={{ color: '#173E4B' }}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      <Tabs
        value={domain === undefined ? 'all' : domain}
        onChange={(e, v) => setDomain(v === 'all' ? undefined : v)}
        sx={{ mb: 2, minHeight: 36, '& .MuiTab-root': { minHeight: 36, textTransform: 'none' } }}
      >
        {DOMAIN_TABS.map(t => (
          <Tab
            key={t.label}
            value={t.value === undefined ? 'all' : t.value}
            label={t.label}
            sx={{ color: navy }}
          />
        ))}
      </Tabs>

      <Alert severity="info" sx={{ mb: 2 }}>
        {domain === 'rating'
          ? 'Rating changes (bulk imports and single node/table edits) always require a ' +
            "different user's approval — a separate queue from product changes."
          : "A different user from the maker must approve a pending change before it " +
            'publishes. Approving applies the change (with an optimistic-concurrency ' +
            'version check); rejecting discards it.'}
      </Alert>

      {data.changeRequestsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {data.changeRequestsError.message || data.changeRequestsError}
        </Alert>
      )}

      {data.changeRequestsLoader ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: '#173E4B' }} />
        </Box>
      ) : (
        <TableContainer component={Card} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={TH}>Target</TableCell>
                <TableCell sx={TH}>Change</TableCell>
                <TableCell sx={TH}>Maker</TableCell>
                <TableCell sx={TH}>Submitted</TableCell>
                <TableCell sx={TH}>Status</TableCell>
                <TableCell sx={{ ...TH, textAlign: 'right' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {crs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box sx={{ textAlign: 'center', py: 6, color: '#9CA3AF' }}>
                      No change requests for this partner.
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {crs.map(cr => {
                const st = STATUS_STYLE[cr.status] || STATUS_STYLE.Stale
                const pending = cr.status === 'Pending'
                return (
                  <TableRow key={cr.guid} hover>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{cr.target_kind || '—'}</Typography>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6B7280' }}>
                        {cr.product_guid || cr.target_guid || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>{cr.change_type || cr.target_scope || '—'}</TableCell>
                    <TableCell sx={{ color: '#374151' }}>{cr.maker || '—'}</TableCell>
                    <TableCell sx={{ color: '#6B7280', fontSize: 12 }}>{fmtDate(cr.created_at)}</TableCell>
                    <TableCell>
                      <Chip
                        label={cr.status}
                        size="small"
                        sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: st.bg, color: st.color }}
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {pending ? (
                        <>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<Check />}
                            disabled={data.changeRequestActionLoader}
                            onClick={() => resolve(cr.guid, 'approve')}
                            sx={{ textTransform: 'none', bgcolor: '#15803D', mr: 1, '&:hover': { bgcolor: '#166534' } }}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Close />}
                            disabled={data.changeRequestActionLoader}
                            onClick={() => resolve(cr.guid, 'reject')}
                            sx={{ textTransform: 'none', color: '#B91C1C', borderColor: '#FECACA' }}
                          >
                            Reject
                          </Button>
                        </>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {cr.checker ? 'by ' + cr.checker : '—'}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={feedbackOpen}
        autoHideDuration={5000}
        onClose={() => dispatch(actions.resetChangeRequestFeedback())}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {feedbackOpen ? (
          <Alert
            severity={data.changeRequestActionError ? 'error' : 'success'}
            variant="filled"
            onClose={() => dispatch(actions.resetChangeRequestFeedback())}
            sx={{ width: '100%' }}
          >
            {data.changeRequestActionError
              ? (data.changeRequestActionError.message || data.changeRequestActionError)
              : 'Change request ' + data.changeRequestActionSuccess + 'd.'}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  )
}

export default connect(state => ({
  genericProductsData: state.genericProducts
}))(ChangeRequestsScreen)
